// =========================================
// Evaluation CSV Export
// =========================================

import { supabase } from '@/lib/supabase';

export interface ExportFilters {
  reviewId?: string;
  phase?: string;
  bucket?: string;
  status?: string;
}

interface ExportRow {
  sourceTitle: string;
  authors: string;
  year: string;
  doi: string;
  aiScore: string;
  bucket: string;
  decision: string;
  decisionSource: string;
  exclusionReason: string;
  phase: string;
  [key: string]: string;
}

/**
 * Generate a CSV string from evaluation and review decision data.
 */
export async function exportEvaluationsCSV(
  filters: ExportFilters,
): Promise<{ csv: string; count: number }> {
  // Fetch review sources with source details
  let sourcesQuery = supabase
    .from('review_sources')
    .select('source_id, current_phase, sources(id, title, authors, publication_year, doi, file_name)');

  if (filters.reviewId) {
    sourcesQuery = sourcesQuery.eq('review_id', filters.reviewId);
  }
  if (filters.phase) {
    sourcesQuery = sourcesQuery.eq('current_phase', filters.phase);
  }

  const { data: reviewSources, error: srcError } = await sourcesQuery;
  if (srcError) throw srcError;

  // Fetch decisions
  let decisionsQuery = supabase
    .from('review_decisions')
    .select('source_id, decision, decision_source, exclusion_reason_category, phase');

  if (filters.reviewId) {
    decisionsQuery = decisionsQuery.eq('review_id', filters.reviewId);
  }

  const { data: decisions, error: decError } = await decisionsQuery;
  if (decError) throw decError;

  // Fetch evaluations with criteria
  const sourceIds = (reviewSources ?? []).map(
    (rs) => (rs as { source_id: string }).source_id,
  );

  let evalData: Array<{
    source_id: string;
    score: number | null;
    evaluation_criteria: Array<{
      criteria_id: string;
      score: number | null;
      confidence: number | null;
    }>;
  }> = [];

  if (sourceIds.length > 0) {
    const { data: evals, error: evalError } = await supabase
      .from('evaluations')
      .select('source_id, score, evaluation_criteria(criteria_id, score, confidence)')
      .in('source_id', sourceIds)
      .eq('status', 'completed');

    if (evalError) throw evalError;
    evalData = (evals ?? []) as typeof evalData;
  }

  // Fetch criteria names
  const criteriaIds = new Set<string>();
  for (const ev of evalData) {
    for (const ec of ev.evaluation_criteria ?? []) {
      criteriaIds.add(ec.criteria_id);
    }
  }

  let criteriaMap = new Map<string, string>();
  if (criteriaIds.size > 0) {
    const { data: criteria } = await supabase
      .from('criteria')
      .select('id, name')
      .in('id', [...criteriaIds]);

    criteriaMap = new Map((criteria ?? []).map((c) => [c.id, c.name]));
  }

  // Build source map
  const sourceMap = new Map<
    string,
    {
      title: string;
      authors: string;
      year: string;
      doi: string;
      phase: string;
    }
  >();

  for (const rs of reviewSources ?? []) {
    const src = (rs as unknown as { sources: { title: string | null; authors: string[] | null; publication_year: number | null; doi: string | null; file_name: string } | null }).sources;
    const sourceId = (rs as { source_id: string }).source_id;
    sourceMap.set(sourceId, {
      title: src?.title ?? src?.file_name ?? '',
      authors: (src?.authors ?? []).join('; '),
      year: src?.publication_year?.toString() ?? '',
      doi: src?.doi ?? '',
      phase: (rs as { current_phase: string }).current_phase,
    });
  }

  // Build decision map (latest per source)
  const decisionMap = new Map<
    string,
    { decision: string; source: string; reason: string }
  >();
  for (const d of decisions ?? []) {
    decisionMap.set(d.source_id, {
      decision: d.decision,
      source: d.decision_source ?? 'human',
      reason: d.exclusion_reason_category ?? '',
    });
  }

  // Build eval map
  const evalMap = new Map<
    string,
    { score: number | null; criteria: Map<string, { score: number | null; confidence: number | null }> }
  >();
  for (const ev of evalData) {
    const criteriaScores = new Map<string, { score: number | null; confidence: number | null }>();
    for (const ec of ev.evaluation_criteria ?? []) {
      criteriaScores.set(ec.criteria_id, { score: ec.score, confidence: ec.confidence });
    }
    evalMap.set(ev.source_id, { score: ev.score, criteria: criteriaScores });
  }

  // Dynamic criteria columns
  const sortedCriteriaIds = [...criteriaIds].sort();

  // Build rows
  const rows: ExportRow[] = [];
  for (const [sourceId, src] of sourceMap) {
    const dec = decisionMap.get(sourceId);
    const ev = evalMap.get(sourceId);

    // Apply filters
    if (filters.bucket) {
      if (ev?.score != null) {
        const aiBucket = ev.score >= 70 ? 'include' : ev.score < 40 ? 'exclude' : 'maybe';
        if (aiBucket !== filters.bucket) continue;
      } else {
        // No AI evaluation — fall back to screening decision
        const decBucket = dec?.decision ?? '';
        if (decBucket && decBucket !== filters.bucket) continue;
      }
    }

    const row: ExportRow = {
      sourceTitle: src.title,
      authors: src.authors,
      year: src.year,
      doi: src.doi,
      aiScore: ev?.score?.toFixed(1) ?? '',
      bucket: ev?.score != null
        ? ev.score >= 70 ? 'include' : ev.score < 40 ? 'exclude' : 'maybe'
        : '',
      decision: dec?.decision ?? '',
      decisionSource: dec?.source ?? '',
      exclusionReason: dec?.reason ?? '',
      phase: src.phase,
    };

    // Add per-criterion scores
    for (const cId of sortedCriteriaIds) {
      const cScore = ev?.criteria.get(cId);
      row[`score_${cId}`] = cScore?.score?.toFixed(1) ?? '';
      row[`conf_${cId}`] = cScore?.confidence?.toFixed(2) ?? '';
    }

    rows.push(row);
  }

  // Build CSV
  const baseHeaders = [
    'Source Title',
    'Authors',
    'Year',
    'DOI',
    'AI Score',
    'Bucket',
    'Decision',
    'Decision Source',
    'Exclusion Reason',
    'Phase',
  ];

  const criteriaHeaders: string[] = [];
  for (const cId of sortedCriteriaIds) {
    const name = criteriaMap.get(cId) ?? cId;
    criteriaHeaders.push(`${name} (Score)`, `${name} (Conf)`);
  }

  const allHeaders = [...baseHeaders, ...criteriaHeaders];

  const csvRows = rows.map((row) => {
    const baseValues = [
      row.sourceTitle,
      row.authors,
      row.year,
      row.doi,
      row.aiScore,
      row.bucket,
      row.decision,
      row.decisionSource,
      row.exclusionReason,
      row.phase,
    ];

    const criteriaValues: string[] = [];
    for (const cId of sortedCriteriaIds) {
      criteriaValues.push(row[`score_${cId}`] ?? '', row[`conf_${cId}`] ?? '');
    }

    return [...baseValues, ...criteriaValues]
      .map((v) => `"${(v ?? '').replace(/"/g, '""')}"`)
      .join(',');
  });

  const csv = [allHeaders.map((h) => `"${h}"`).join(','), ...csvRows].join('\n');

  return { csv, count: rows.length };
}
